package dev.lingvo.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.nav.AppRoute
import dev.lingvo.site.state.ProjectDirectory
import dev.lingvo.site.state.WorkspaceSelection
import kotlinx.browser.document

/**
 * The tab title, for every `/app` route at once.
 *
 * **Not one route in the app set one.** Every marketing and auth page calls `document.title` in its
 * own `LaunchedEffect`; all forty-odd app routes did not, so whatever the last public page happened
 * to set was still hanging over the workspace — a person with the editor, QA and billing open in
 * three tabs read "Lingvo" on all three and had to click to find out which was which. The tab title
 * is also the tooltip a browser shows on hover, so it is the only label those tabs have.
 *
 * The fix belongs here rather than in forty-five `LaunchedEffect`s because the shell already knows
 * the answer: [AppRoute.sectionLabel] is the same function the breadcrumb's last crumb uses, and
 * [ProjectDirectory] already holds the project's real name. The title is the trail, read backwards —
 * most specific first, because a tab strip truncates from the right and the first word is the only
 * one guaranteed to survive.
 *
 *     Strings · Lingvo Mobile — Lingvo
 *     German · Lingvo Mobile — Lingvo
 *     Members · Maya's workspace — Lingvo
 *     Lingvo — localization without word limits      (the dashboard, which has no section)
 *
 * The suffix is always "Lingvo" so a tab is identifiable as this product even when the front of the
 * string is cut, which is the whole job of a tab title in a strip of twenty.
 */
@Composable
fun AppDocumentTitle(currentPath: String) {
    val workspace = WorkspaceSelection.current()
    val slug = AppRoute.projectSlug(currentPath)
    // Read during composition, not inside the effect: `ProjectDirectory.find` is backed by
    // Compose state, and reading it in the effect body would mean the title stayed on the slug
    // after the directory loaded and the name arrived.
    val projectName = slug?.let { ProjectDirectory.find(it)?.name ?: it }
    val section = AppRoute.sectionLabel(currentPath)
    val scopeName = projectName ?: workspace?.name

    LaunchedEffect(section, scopeName) {
        document.title = when {
            section != null && scopeName != null -> "$section · $scopeName — Lingvo"
            section != null -> "$section — Lingvo"
            scopeName != null -> "$scopeName — Lingvo"
            else -> tr("app_document_title.default", "Lingvo — localization without word limits")
        }
    }
}
